import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, RotateCcw, Loader2, ClipboardEdit, Camera } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const uid = (p) => `${p}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy.map((x, idx) => ({ ...x, order: idx }));
};

const OptionRow = ({ opt, idx, total, onChange, onMove, onRemove, testId }) => (
  <div className="flex flex-wrap sm:flex-nowrap items-center gap-2" data-testid={testId}>
    <Input value={opt.label} onChange={(e) => onChange({ ...opt, label: e.target.value })} placeholder="Testo opzione" maxLength={200} className="text-base w-full sm:flex-1" data-testid={`${testId}-label`} />
    <Switch checked={opt.enabled !== false} onCheckedChange={(v) => onChange({ ...opt, enabled: v })} data-testid={`${testId}-enabled`} />
    <label className={`flex items-center gap-1 text-xs whitespace-nowrap cursor-pointer px-2 py-1 rounded-full border ${opt.marks_new_client ? 'border-emerald-500/50 text-emerald-400 bg-emerald-500/10' : 'border-border text-muted-foreground'}`} title="Mostra l'etichetta 'Nuovo cliente' in agenda quando il cliente sceglie questa opzione">
      <input type="checkbox" className="accent-emerald-500" checked={!!opt.marks_new_client} onChange={(e) => onChange({ ...opt, marks_new_client: e.target.checked })} data-testid={`${testId}-new-client`} />
      Nuovo cliente
    </label>
    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" disabled={idx === 0} onClick={() => onMove(-1)} data-testid={`${testId}-up`}><ArrowUp className="h-3.5 w-3.5" /></Button>
    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" disabled={idx === total - 1} onClick={() => onMove(1)} data-testid={`${testId}-down`}><ArrowDown className="h-3.5 w-3.5" /></Button>
    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={onRemove} data-testid={`${testId}-remove`}><Trash2 className="h-3.5 w-3.5" /></Button>
  </div>
);

const PhotoUploadSettings = ({ value, onChange }) => (
  <Card data-testid="photo-upload-settings"><CardContent className="p-4 space-y-3">
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm font-semibold flex items-center gap-1.5"><Camera className="h-4 w-4 text-accent" /> Foto di riferimento (facoltative, max 3)</span>
      <Switch checked={value.enabled !== false} onCheckedChange={(v) => onChange({ ...value, enabled: v })} data-testid="photo-upload-enabled" />
    </div>
    <div className="grid grid-cols-[70px_1fr] gap-2">
      <div><Label className="text-xs">Emoji</Label><Input value={value.emoji || ''} maxLength={10} onChange={(e) => onChange({ ...value, emoji: e.target.value })} className="text-base text-center" data-testid="photo-upload-emoji" /></div>
      <div><Label className="text-xs">Testo</Label><Input value={value.label || ''} maxLength={200} onChange={(e) => onChange({ ...value, label: e.target.value })} className="text-base" data-testid="photo-upload-label" /></div>
    </div>
    <div><Label className="text-xs">Suggerimento</Label><Input value={value.hint || ''} maxLength={300} onChange={(e) => onChange({ ...value, hint: e.target.value })} className="text-base" data-testid="photo-upload-hint" /></div>
    <p className="text-xs text-muted-foreground">Le foto sono visibili solo a te e al cliente che le ha caricate.</p>
  </CardContent></Card>
);

const QuestionEditor = ({ q, idx, total, onChange, onMove, onRemove }) => {
  const tid = `form-question-${idx}`;
  const options = q.options || [];
  const setOptions = (opts) => onChange({ ...q, options: opts });
  return (
    <Card className="border-border" data-testid={tid}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-muted-foreground">Domanda {idx + 1}</span>
          <div className="flex items-center gap-1">
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" disabled={idx === 0} onClick={() => onMove(-1)} data-testid={`${tid}-up`}><ArrowUp className="h-4 w-4" /></Button>
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" disabled={idx === total - 1} onClick={() => onMove(1)} data-testid={`${tid}-down`}><ArrowDown className="h-4 w-4" /></Button>
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={onRemove} data-testid={`${tid}-delete`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="grid grid-cols-[70px_1fr] gap-2">
          <div><Label className="text-xs">Emoji</Label><Input value={q.emoji || ''} maxLength={10} onChange={(e) => onChange({ ...q, emoji: e.target.value })} className="text-base text-center" data-testid={`${tid}-emoji`} /></div>
          <div><Label className="text-xs">Testo domanda</Label><Input value={q.label} maxLength={300} onChange={(e) => onChange({ ...q, label: e.target.value })} className="text-base" data-testid={`${tid}-label`} /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={q.type} onValueChange={(v) => onChange({ ...q, type: v, options: v === 'single_choice' && !options.length ? [{ id: uid('o'), label: '', enabled: true, order: 0 }] : options })}>
              <SelectTrigger data-testid={`${tid}-type`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="single_choice">Scelta singola</SelectItem>
                <SelectItem value="text">Testo libero</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!q.required} onCheckedChange={(v) => onChange({ ...q, required: v })} data-testid={`${tid}-required`} /> Obbligatoria</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={q.enabled !== false} onCheckedChange={(v) => onChange({ ...q, enabled: v })} data-testid={`${tid}-enabled`} /> Visibile</label>
        </div>
        {q.type === 'single_choice' ? (
          <div className="space-y-2">
            <Label className="text-xs">Opzioni (interruttore = visibile)</Label>
            {options.map((o, oi) => (
              <OptionRow key={o.id} opt={o} idx={oi} total={options.length} testId={`${tid}-option-${oi}`}
                onChange={(no) => setOptions(options.map((x) => (x.id === o.id ? no : x)))}
                onMove={(d) => setOptions(move(options, oi, d))}
                onRemove={() => setOptions(options.filter((x) => x.id !== o.id))} />
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setOptions([...options, { id: uid('o'), label: '', enabled: true, order: options.length }])} data-testid={`${tid}-add-option`}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi opzione
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Testo di esempio</Label><Input value={q.placeholder || ''} maxLength={200} onChange={(e) => onChange({ ...q, placeholder: e.target.value })} className="text-base" data-testid={`${tid}-placeholder`} /></div>
            <div><Label className="text-xs">Max caratteri</Label><Input type="number" min={10} max={1000} value={q.max_length || 500} onChange={(e) => onChange({ ...q, max_length: Math.min(1000, Math.max(10, parseInt(e.target.value, 10) || 500)) })} className="text-base" data-testid={`${tid}-maxlength`} /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Suggerimento sotto il campo</Label><Input value={q.hint || ''} maxLength={200} onChange={(e) => onChange({ ...q, hint: e.target.value })} className="text-base" data-testid={`${tid}-hint`} /></div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function AdminBookingFormSettings() {
  const { getAuthHeaders } = useAuth();
  const [config, setConfigState] = useState(null);
  const [dirty, setDirty] = useState(false);
  const setConfig = (c) => { setConfigState(c); setDirty(true); };
  const loadConfig = (c) => { setConfigState(c); setDirty(false); };
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [deleteIdx, setDeleteIdx] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/booking-form/config`, { headers: getAuthHeaders() })
      .then((r) => loadConfig({ ...r.data, questions: [...(r.data.questions || [])].sort((a, b) => (a.order || 0) - (b.order || 0)) }))
      .catch(() => toast.error('Errore nel caricamento del modulo'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setQuestions = (questions) => setConfig({ ...config, questions: questions.map((q, i) => ({ ...q, order: i })) });

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/booking-form/config`, config, { headers: getAuthHeaders() });
      loadConfig({ ...r.data, questions: [...r.data.questions].sort((a, b) => (a.order || 0) - (b.order || 0)) });
      toast.success('Modulo prenotazione salvato');
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Dati non validi: controlla la lunghezza dei testi');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    try {
      const r = await axios.post(`${API}/admin/booking-form/reset`, {}, { headers: getAuthHeaders() });
      loadConfig(r.data); toast.success('Modulo ripristinato ai valori predefiniti');
    } catch { toast.error('Errore nel ripristino'); }
    setConfirmReset(false);
  };

  const addQuestion = (type) => setQuestions([...config.questions, {
    id: uid('q'), type, emoji: '', label: '', required: false, enabled: true,
    options: type === 'single_choice' ? [{ id: uid('o'), label: '', enabled: true, order: 0 }] : [],
    placeholder: '', hint: '', max_length: 500,
  }]);

  if (!config) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-booking-form-settings">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2"><ClipboardEdit className="h-5 w-5 text-accent" /> Modulo Prenotazione</h2>
        <label className="flex items-center gap-2 text-sm font-medium">
          <Switch checked={!!config.enabled} onCheckedChange={(v) => setConfig({ ...config, enabled: v })} data-testid="form-enabled-switch" />
          {config.enabled ? 'Sezione attiva' : 'Sezione disattivata'}
        </label>
      </div>
      <p className="text-sm text-muted-foreground">Queste domande compaiono ai clienti durante la prenotazione, prima della conferma. Non cambiano prezzo né durata del servizio. Le risposte già salvate negli appuntamenti restano invariate anche se modifichi le domande.</p>

      <Card><CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-[70px_1fr] gap-2">
          <div><Label className="text-xs">Emoji</Label><Input value={config.emoji || ''} maxLength={10} onChange={(e) => setConfig({ ...config, emoji: e.target.value })} className="text-base text-center" data-testid="form-title-emoji" /></div>
          <div><Label className="text-xs">Titolo sezione</Label><Input value={config.title || ''} maxLength={150} onChange={(e) => setConfig({ ...config, title: e.target.value })} className="text-base" data-testid="form-title-input" /></div>
        </div>
        <div><Label className="text-xs">Descrizione</Label><Textarea value={config.description || ''} maxLength={500} onChange={(e) => setConfig({ ...config, description: e.target.value })} rows={2} className="text-base" data-testid="form-description-input" /></div>
      </CardContent></Card>

      <div className="space-y-3">
        {config.questions.length > 0 && <p className="text-xs text-muted-foreground">Spunta "Nuovo cliente" su un'opzione (es. "Sì, è la prima volta") per mostrare l'etichetta in agenda. Resta collegata anche se cambi il testo dell'opzione.</p>}
        {config.questions.map((q, i) => (
          <QuestionEditor key={q.id} q={q} idx={i} total={config.questions.length}
            onChange={(nq) => setQuestions(config.questions.map((x) => (x.id === q.id ? nq : x)))}
            onMove={(d) => setQuestions(move(config.questions, i, d))}
            onRemove={() => setDeleteIdx(i)} />
        ))}
        {config.questions.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Nessuna domanda. Aggiungine una qui sotto.</p>}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => addQuestion('single_choice')} data-testid="add-choice-question-btn"><Plus className="h-4 w-4 mr-1" /> Domanda a scelta</Button>
        <Button variant="outline" onClick={() => addQuestion('text')} data-testid="add-text-question-btn"><Plus className="h-4 w-4 mr-1" /> Domanda a testo libero</Button>
      </div>

      <PhotoUploadSettings value={config.photo_upload || {}} onChange={(pu) => setConfig({ ...config, photo_upload: pu })} />

      <div className="flex flex-wrap items-center gap-2 justify-end border-t border-border pt-4">
        {dirty && <span className="text-sm text-yellow-500 mr-auto" data-testid="form-unsaved-indicator">Modifiche non salvate</span>}
        <Button variant="outline" onClick={() => setConfirmReset(true)} data-testid="reset-form-btn"><RotateCcw className="h-4 w-4 mr-1" /> Ripristina predefinito</Button>
        <Button onClick={save} disabled={saving} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="save-form-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salva modifiche
        </Button>
      </div>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Ripristinare il modulo?</AlertDialogTitle>
            <AlertDialogDescription>Le domande torneranno a quelle predefinite. Le risposte già salvate negli appuntamenti non vengono toccate.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={reset} data-testid="confirm-reset-form-btn">Ripristina</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteIdx !== null} onOpenChange={(o) => { if (!o) setDeleteIdx(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Eliminare questa domanda?</AlertDialogTitle>
            <AlertDialogDescription>Ricorda di premere "Salva modifiche" per applicare. Le risposte già date restano negli appuntamenti passati.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setQuestions(config.questions.filter((_, i) => i !== deleteIdx)); setDeleteIdx(null); }} data-testid="confirm-delete-question-btn">Elimina</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
